'use client';

import { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';

/**
 * The household on a map, the way the locations finder draws campuses:
 * Google Maps with every control off and points of interest hidden, the
 * campus as its brand mark in a white card, home as a navy pin, the
 * seasonal address as a teal one. The map fits whatever pins exist.
 */

const TEAL = '#299c8f';
const NAVY = '#1c2b39';

declare global {
  interface Window {
    google?: typeof google;
  }
}

export interface MapPinSpec {
  key: string;
  kind: 'home' | 'seasonal' | 'campus';
  /** MP's geocode when it has one */
  lat: number | null;
  lng: number | null;
  /** Geocoded here when MP has not got to it yet (a just-saved address) */
  address?: string;
  title: string;
  /** Campus pins carry the campus mark */
  svgUrl?: string | null;
}

type Placed = MapPinSpec & { lat: number; lng: number };

const geocodeCache = new Map<string, { lat: number; lng: number } | null>();

/** Google's geocoder for the addresses MP has not placed yet; one lookup per address, ever. */
async function place(pin: MapPinSpec): Promise<Placed | null> {
  if (pin.lat !== null && pin.lng !== null) return pin as Placed;
  if (!pin.address) return null;
  if (geocodeCache.has(pin.address)) {
    const hit = geocodeCache.get(pin.address);
    return hit ? { ...pin, ...hit } : null;
  }
  try {
    const { results } = await new google.maps.Geocoder().geocode({ address: pin.address });
    const loc = results?.[0]?.geometry?.location;
    const hit = loc ? { lat: loc.lat(), lng: loc.lng() } : null;
    geocodeCache.set(pin.address, hit);
    return hit ? { ...pin, ...hit } : null;
  } catch {
    geocodeCache.set(pin.address, null);
    return null;
  }
}

let loader: Promise<void> | null = null;

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (window.google?.maps?.Map) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise((resolve, reject) => {
    const settle = () => {
      const check = window.setInterval(() => {
        if (window.google?.maps?.Map) {
          window.clearInterval(check);
          resolve();
        }
      }, 50);
    };
    // The locations finder on the same page may have loaded it already.
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      settle();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = settle;
    script.onerror = () => {
      loader = null;
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });
  return loader;
}

/** A teardrop pin with a glyph, as a data URL. */
function glyphPin(fill: string, glyph: 'home' | 'plane'): google.maps.Icon {
  const path =
    glyph === 'home'
      ? '<path d="M16 8.5l-6 5.2V21h4.2v-4h3.6v4H22v-7.3l-6-5.2z" fill="white"/>'
      : '<path d="M22.5 9.5c.8-.8.8-1.3.5-1.6-.3-.3-.8-.3-1.6.5l-3.2 3.2-6.4-2 1.1 1.1 4.1 2.6-2.7 2.7-2.6-.5.8.8 1.6 1.2 1.2 1.6.8.8-.5-2.6 2.7-2.7 2.6 4.1 1.1 1.1-2-6.4 3.2-3.2z" fill="white"/>';
  const svg = `<svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="${fill}"/>
    <circle cx="16" cy="15" r="10" fill="${fill}" stroke="white" stroke-width="2"/>
    ${path}
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(36, 47),
    anchor: new google.maps.Point(18, 47),
  };
}

const markerCache = new Map<string, string>();

/** The campus mark inside a white card with a pointer, painted to a canvas (the locations finder's marker). */
function campusMarker(svgUrl: string, borderColor: string, size: number): Promise<string> {
  const cacheKey = `${svgUrl}:${borderColor}:${size}`;
  const cached = markerCache.get(cacheKey);
  if (cached) return Promise.resolve(cached);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const padding = 6;
      const pointerHeight = 10;
      const cardW = size;
      const cardH = size;
      const totalW = cardW + padding * 2;
      const totalH = cardH + padding * 2 + pointerHeight;
      const r = 10;
      const canvas = document.createElement('canvas');
      const dpr = window.devicePixelRatio || 1;
      canvas.width = totalW * dpr;
      canvas.height = totalH * dpr;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(dpr, dpr);
      ctx.shadowColor = 'rgba(0,0,0,0.2)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 2;
      const x = padding;
      const y = padding;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + cardW - r, y);
      ctx.quadraticCurveTo(x + cardW, y, x + cardW, y + r);
      ctx.lineTo(x + cardW, y + cardH - r);
      ctx.quadraticCurveTo(x + cardW, y + cardH, x + cardW - r, y + cardH);
      ctx.lineTo(totalW / 2 + 6, y + cardH);
      ctx.lineTo(totalW / 2, y + cardH + pointerHeight);
      ctx.lineTo(totalW / 2 - 6, y + cardH);
      ctx.lineTo(x + r, y + cardH);
      ctx.quadraticCurveTo(x, y + cardH, x, y + cardH - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      ctx.fillStyle = 'white';
      ctx.fill();
      ctx.shadowColor = 'transparent';
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.save();
      const inset = 3;
      ctx.beginPath();
      ctx.rect(x + inset, y + inset, cardW - inset * 2, cardH - inset * 2);
      ctx.clip();
      const drawW = cardW - inset * 2;
      const drawH = cardH - inset * 2;
      const scale = Math.max(drawW / img.width, drawH / img.height);
      const sw = img.width * scale;
      const sh = img.height * scale;
      ctx.drawImage(img, x + inset + (drawW - sw) / 2, y + inset + (drawH - sh) / 2, sw, sh);
      ctx.restore();
      const url = canvas.toDataURL();
      markerCache.set(cacheKey, url);
      resolve(url);
    };
    img.onerror = () => resolve('');
    img.src = svgUrl;
  });
}

export function HomeMap({
  apiKey,
  pins,
  focusKeys,
  padding = { top: 56, right: 40, bottom: 32, left: 40 },
  className = '',
}: {
  apiKey: string | undefined;
  pins: MapPinSpec[];
  /** Frame only these pins (the rest still show); all of them when omitted */
  focusKeys?: string[];
  /** Room kept clear around the framed pins (the profile's card puts a header over the top) */
  padding?: google.maps.Padding;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  // What the map is framing, so a resize can frame it again.
  const frameRef = useRef<(() => void) | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [failed, setFailed] = useState(false);
  const pinKey =
    pins
      .map((p) => `${p.kind}:${p.lat ?? ''}:${p.lng ?? ''}:${p.address ?? ''}:${p.svgUrl ?? ''}`)
      .join('|') + `#${(focusKeys ?? []).join(',')}`;

  useEffect(() => {
    if (!apiKey || !containerRef.current || pins.length === 0) return;
    let cancelled = false;

    async function draw() {
      try {
        await loadGoogleMaps(apiKey!);
      } catch {
        setFailed(true);
        return;
      }
      if (cancelled || !containerRef.current || !window.google?.maps?.Map) return;

      const placed = (await Promise.all(pins.map(place))).filter((p): p is Placed => p !== null);
      if (cancelled || !containerRef.current) return;
      if (placed.length === 0) {
        setFailed(true);
        return;
      }

      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(containerRef.current, {
          center: { lat: placed[0].lat, lng: placed[0].lng },
          zoom: 11,
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          keyboardShortcuts: false,
          gestureHandling: 'cooperative',
          styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
        });
      }
      const map = mapRef.current;

      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];

      const bounds = new google.maps.LatLngBounds();
      const framed = focusKeys?.length ? placed.filter((p) => focusKeys.includes(p.key)) : placed;
      for (const pin of framed) bounds.extend({ lat: pin.lat, lng: pin.lng });
      for (const pin of placed) {
        const position = { lat: pin.lat, lng: pin.lng };
        const marker = new google.maps.Marker({
          map,
          position,
          title: pin.title,
          icon:
            pin.kind === 'home'
              ? glyphPin(NAVY, 'home')
              : pin.kind === 'seasonal'
                ? glyphPin(TEAL, 'plane')
                : undefined,
          zIndex: pin.kind === 'home' ? 999 : pin.kind === 'seasonal' ? 998 : 1,
        });
        if (pin.kind === 'campus' && pin.svgUrl) {
          void campusMarker(pin.svgUrl, TEAL, 44).then((url) => {
            if (cancelled || !url) return;
            const totalW = 44 + 12;
            const totalH = 44 + 12 + 10;
            marker.setIcon({
              url,
              scaledSize: new google.maps.Size(totalW, totalH),
              anchor: new google.maps.Point(totalW / 2, totalH),
            });
          });
        }
        markersRef.current.push(marker);
      }

      // Two pins across a state (home in Troy, winter in Naples) still both
      // show; one pin gets a neighbourhood, not a continent.
      // After layout settles: a map made inside a sheet that is still sliding
      // open measures itself before it has a size, and fitting to that box
      // zooms out to half the state.
      // A timer, not requestAnimationFrame: rAF never fires in a tab that is
      // not in front, and the map would keep its first, wrong framing.
      frameRef.current = () =>
        window.setTimeout(() => {
          if (framed.length > 1) {
            map.fitBounds(bounds, padding);
          } else if (framed[0]) {
            map.setCenter({ lat: framed[0].lat, lng: framed[0].lng });
            map.setZoom(13);
          }
        }, 60);
      frameRef.current();
      // And once more when Google says the map is drawn at its real size.
      google.maps.event.addListenerOnce(map, 'idle', () => frameRef.current?.());
    }

    void draw();
    return () => {
      cancelled = true;
    };
    // pinKey captures every coordinate; pins itself is a fresh array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, pinKey]);

  // Framed while its box was still growing (a sheet sliding open), the map
  // zooms out to fit a box that is not there yet. Frame it again whenever the
  // box settles at a new size.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let last = '';
    const ro = new ResizeObserver(([entry]) => {
      const size = `${Math.round(entry.contentRect.width)}x${Math.round(entry.contentRect.height)}`;
      if (size === last) return;
      last = size;
      frameRef.current?.();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [failed]);

  if (!apiKey || failed || pins.length === 0) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-center ${className}`}>
        <div className="flex flex-col items-center gap-2 px-6">
          <MapPin className="h-8 w-8 text-gray-300" strokeWidth={2} />
          <p className="text-sm font-semibold text-gray-500">
            {pins.length === 0
              ? 'Add an address to see it on the map'
              : 'The map is taking a break'}
          </p>
        </div>
      </div>
    );
  }
  return <div ref={containerRef} className={className} />;
}
